import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.uxauditpro.com';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;

/**
 * SEO component — drop into any page to set per-page meta tags.
 *
 * Props:
 *   title        — page title (appended with " | UXAuditPro")
 *   description  — meta description (max ~160 chars)
 *   canonical    — canonical URL path e.g. "/ux-auditor"
 *   ogImage      — absolute URL for OG image (defaults to logo)
 *   ogType       — "website" | "article" | "product" (default "website")
 *   noIndex      — set true for auth/dashboard pages
 *   structuredData — JSON-LD object or array to inject as <script type="application/ld+json">
 */
const OG_IMAGE = `${BASE_URL}/og-image.png`;

const SEO = ({
  title,
  fullTitle: rawFullTitle,
  description,
  canonical,
  ogTitle,
  ogImage = OG_IMAGE,
  ogType = 'website',
  noIndex = false,
  noCanonical = false,
  structuredData,
}) => {
  const fullTitle = rawFullTitle || (title ? `${title} | UXAuditPro` : 'UXAuditPro: AI-Powered UX Audit Tool | Instant Reports');
  // Trailing-slash convention: the canonical (and og:url) always end in a slash so they
  // match the served URL and never point at a URL that 301s away. Derived from the path,
  // so it cannot drift.
  const rawPath = canonical || '/';
  const canonPath = rawPath === '/' ? '/' : `/${rawPath.replace(/^\/+|\/+$/g, '')}/`;
  const canonicalUrl = `${BASE_URL}${canonPath}`;
  const ogTitleFinal = ogTitle || fullTitle;

  return (
    <Helmet>
      {/* Basic */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {/* A 404 (noCanonical) must emit no canonical at all, never one pointing elsewhere. */}
      {!noCanonical && <link rel="canonical" href={canonicalUrl} />}
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow" />
      }

      {/* Open Graph */}
      <meta property="og:site_name" content="UXAuditPro" />
      <meta property="og:title" content={ogTitleFinal} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitleFinal} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
